import { useState, useEffect, useCallback, useRef } from 'react'
import { apiClient } from '@/services/api'
import { useErrorHandler } from '@/hooks/useAuth'
import type { AuditInfoModel, Paginated, PaginationParams } from '@/types'

export interface TrolleyDrawer {
  name: string
  id: number
  total_tools: number
}

export interface Trolley extends AuditInfoModel {
  name: string
  drawer_count: number
  active_drawer_count: number
  drawers: TrolleyDrawer[]
  description: string
}

type UseGetTrolleysReturn = Paginated<Trolley> & {
  isLoading: boolean
  error: Nullable<string>
  refetch: () => Promise<void>
}

export function useGetTrolleys(
  params?: PaginationParams
): UseGetTrolleysReturn {
  const [trolleys, setTrolleys] = useState<Trolley[]>([])
  const [pagination, setPagination] = useState({
    page: params?.page ?? 1,
    page_size: params?.page_size ?? 10,
    total: 0,
    total_pages: 0,
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Nullable<string>>(null)
  const { handleError } = useErrorHandler()
  const handleErrorRef = useRef(handleError)

  useEffect(() => {
    handleErrorRef.current = handleError
  }, [handleError])

  const fetchTrolleys = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiClient.get<Paginated<Trolley>>('/trolleys/', {
        params: {
          page: params?.page ?? 1,
          page_size: params?.page_size ?? 10,
        },
      })
      setTrolleys(response.data.items)
      setPagination({
        page: response.data.page,
        page_size: response.data.page_size,
        total: response.data.total,
        total_pages: response.data.total_pages,
      })
    } catch (err) {
      setError(handleErrorRef.current(err))
      setTrolleys([])
      setPagination({
        page: params?.page ?? 1,
        page_size: params?.page_size ?? 10,
        total: 0,
        total_pages: 0,
      })
    } finally {
      setIsLoading(false)
    }
  }, [params?.page, params?.page_size])

  useEffect(() => {
    fetchTrolleys()
  }, [fetchTrolleys])

  return {
    items: trolleys,
    page: pagination.page,
    page_size: pagination.page_size,
    total: pagination.total,
    total_pages: pagination.total_pages,
    isLoading,
    error,
    refetch: fetchTrolleys,
  }
}
